import React, { useState } from 'react';
import { IcoClose, IcoUsers, IcoCheck, IcoAlertTriangle } from '../dashboard/DashIcons';

const PLANS = [
  {
    id:           'starter',
    name:         'Starter',
    badge:        null,
    description:  'For small teams just getting started',
    monthlyPrice: 29,
    annualPrice:  23,
    employeeRange: '1–30 employees',
    hardCap:      34,
    color:        '#22c55e',
    accent:       '#eff6ff',
    features: [
      'Up to 30 employees (hard cap 34)',
      'Time tracking & manual entry',
      'Clock in / clock out',
      'Scheduling',
      'PTO tracking',
      'Payroll processing',
      'Mobile app access',
      'Email support',
    ],
    missing: [
      'Reporting & analytics',
      'EVV compliance',
      'Custom integrations',
    ],
  },
  {
    id:           'professional',
    name:         'Professional',
    badge:        'Most Popular',
    description:  'For growing healthcare facilities',
    monthlyPrice: 69,
    annualPrice:  55,
    employeeRange: '31–100 employees',
    hardCap:      115,
    color:        '#22c55e',
    accent:       '#eef2ff',
    features: [
      'Up to 100 employees (hard cap 115)',
      'Everything in Starter',
      'Reporting & analytics',
      'Labor cost exports',
      'EVV compliance',
      'Priority email support',
    ],
    missing: [
      'Custom integrations',
      'Dedicated account manager',
    ],
  },
  {
    id:           'growth',
    name:         'Growth',
    badge:        null,
    description:  'For larger care organizations',
    monthlyPrice: 149,
    annualPrice:  119,
    employeeRange: '101–150 employees',
    hardCap:      172,
    color:        '#7c3aed',
    accent:       '#f5f3ff',
    features: [
      'Up to 150 employees (hard cap 172)',
      'Everything in Professional',
      'Priority support',
      'Multi-location support',
    ],
    missing: [
      'Custom integrations',
      'Dedicated account manager',
    ],
  },
  {
    id:           'enterprise',
    name:         'Enterprise',
    badge:        null,
    description:  'For large organizations — custom pricing',
    monthlyPrice: null,
    annualPrice:  null,
    employeeRange: '151+ employees',
    hardCap:      null,
    color:        '#0f172a',
    accent:       '#f8fafc',
    features: [
      'Unlimited employees',
      'Everything in Growth',
      'Custom integrations',
      'Dedicated account manager',
      'SLA guarantee',
      'Phone & chat support',
      'Custom onboarding',
    ],
    missing: [],
  },
];

export default function ChangePlanModal({ currentPlan = 'starter', onClose, onSelectPlan }) {
  const [billing, setBilling] = useState('monthly');
  const [selected, setSelected] = useState(currentPlan);
  const [confirming, setConfirming] = useState(false);

  const selectedPlan     = PLANS.find(p => p.id === selected);
  const currentPlanData  = PLANS.find(p => p.id === currentPlan);

  const handleConfirm = () => {
    if (selected === currentPlan) { onClose(); return; }
    setConfirming(true);
  };

  const handleFinalConfirm = () => {
    if (onSelectPlan) onSelectPlan(selected, billing);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(15, 23, 42, 0.6)',
      backdropFilter: 'blur(4px)',
      zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        background: 'white', borderRadius: '20px',
        width: '100%', maxWidth: '980px', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 25px 60px rgba(0,0,0,0.2)',
      }}>

        {/* Header */}
        <div style={{
          padding: '2rem 2rem 1.5rem',
          borderBottom: '1px solid #f1f5f9',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          position: 'sticky', top: 0, background: 'white', zIndex: 10,
          borderRadius: '20px 20px 0 0',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.02em' }}>
              Change Your Plan
            </h2>
            <p style={{ margin: '0.25rem 0 0', color: '#64748b', fontSize: '0.9rem' }}>
              Currently on <strong style={{ color: '#22c55e' }}>{currentPlanData?.name}</strong>
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: '#f1f5f9', borderRadius: '10px', padding: '4px' }}>
              {['monthly', 'annual'].map(cycle => (
                <button key={cycle} onClick={() => setBilling(cycle)} style={{
                  padding: '0.4rem 0.9rem', borderRadius: '7px', border: 'none', cursor: 'pointer',
                  fontSize: '0.8rem', fontWeight: '600',
                  background: billing === cycle ? 'white' : 'transparent',
                  color: billing === cycle ? '#0f172a' : '#64748b',
                  boxShadow: billing === cycle ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s',
                }}>
                  {cycle === 'annual' ? 'Annual' : 'Monthly'}
                  {cycle === 'annual' && <span style={{ marginLeft: '4px', color: '#10b981', fontSize: '0.7rem' }}>–20%</span>}
                </button>
              ))}
            </div>
            <button onClick={onClose} style={{
              background: '#f1f5f9', border: 'none', borderRadius: '8px',
              width: '36px', height: '36px', cursor: 'pointer',
              fontSize: '1.1rem', color: '#64748b',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><IcoClose size={16}/></button>
          </div>
        </div>

        {/* Plan cards */}
        <div style={{ padding: '1.5rem 2rem', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
          {PLANS.map(plan => {
            const isSelected = selected === plan.id;
            const isCurrent  = currentPlan === plan.id;
            const price = plan.monthlyPrice === null ? null
              : billing === 'annual' ? plan.annualPrice : plan.monthlyPrice;

            return (
              <div key={plan.id} onClick={() => setSelected(plan.id)} style={{
                borderRadius: '14px',
                border: `2px solid ${isSelected ? plan.color : '#e2e8f0'}`,
                padding: '1.25rem', cursor: 'pointer',
                background: isSelected ? plan.accent : 'white',
                transition: 'all 0.2s', position: 'relative',
                transform: isSelected ? 'translateY(-2px)' : 'none',
                boxShadow: isSelected ? `0 8px 24px ${plan.color}22` : '0 1px 3px rgba(0,0,0,0.05)',
              }}>
                {plan.badge && (
                  <div style={{
                    position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #22c55e, #0f4d2e)',
                    color: 'white', fontSize: '0.65rem', fontWeight: '800',
                    padding: '0.2rem 0.7rem', borderRadius: '999px',
                    whiteSpace: 'nowrap', letterSpacing: '0.05em', textTransform: 'uppercase',
                  }}>{plan.badge}</div>
                )}
                {isCurrent && (
                  <div style={{
                    position: 'absolute', top: '-11px', right: '12px',
                    background: '#10b981', color: 'white',
                    fontSize: '0.65rem', fontWeight: '700',
                    padding: '0.2rem 0.6rem', borderRadius: '999px',
                  }}>Current</div>
                )}

                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ fontWeight: '800', fontSize: '1rem', color: plan.color, marginBottom: '0.15rem' }}>
                    {plan.name}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '0.6rem' }}>
                    {plan.description}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                    <span style={{ fontSize: '1.75rem', fontWeight: '900', color: '#0f172a', letterSpacing: '-0.03em' }}>
                      {price === null ? 'Custom' : `$${price}`}
                    </span>
                    {price !== null && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>/mo</span>}
                  </div>
                  {price === null && (
                    <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.2rem' }}>starts ~$249/mo</div>
                  )}
                  {billing === 'annual' && price !== null && (
                    <div style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: '600', marginTop: '0.2rem' }}>
                      Billed ${price * 12}/yr
                    </div>
                  )}
                </div>

                {/* Employee range + hard cap */}
                <div style={{ fontSize: '0.72rem', color: '#374151', fontWeight: '600', marginBottom: '0.2rem' }}>
                  <><IcoUsers size={14}/> {plan.employeeRange}</>
                </div>
                {plan.hardCap && (
                  <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginBottom: '0.6rem' }}>
                    Hard cap: {plan.hardCap} employees
                  </div>
                )}

                <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '0.6rem 0' }} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                  {plan.features.map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', fontSize: '0.72rem', color: '#374151' }}>
                      <span style={{ flexShrink: 0, marginTop: '1px' }}><IcoCheck size={12} color="#10b981"/></span>
                      {f}
                    </div>
                  ))}
                  {plan.missing.map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', fontSize: '0.72rem', color: '#cbd5e1' }}>
                      <span style={{ flexShrink: 0, marginTop: '1px' }}>–</span>
                      {f}
                    </div>
                  ))}
                </div>

                <div style={{
                  marginTop: '1rem', textAlign: 'center', padding: '0.5rem', borderRadius: '8px',
                  background: isSelected ? plan.color : '#f8fafc',
                  color: isSelected ? 'white' : '#94a3b8',
                  fontSize: '0.78rem', fontWeight: '700', transition: 'all 0.15s',
                }}>
                  {isSelected ? <><IcoCheck size={12}/> Selected</> : 'Select'}
                </div>
              </div>
            );
          })}
        </div>

        {/* Confirmation dialog */}
        {confirming && selected !== currentPlan && (
          <div style={{ margin: '0 2rem 1.5rem', padding: '1.25rem', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '12px' }}>
            <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}><IcoAlertTriangle size={14} color="#92400e"/> Confirm Plan Change</div>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#78350f' }}>
              You're switching from <strong>{currentPlanData?.name}</strong> to <strong>{selectedPlan?.name}</strong>.
              {billing === 'annual' ? ' Your annual billing will be updated at the next cycle.' : ' Your monthly billing will change immediately.'}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={handleFinalConfirm} style={{
                padding: '0.6rem 1.5rem', borderRadius: '8px', border: 'none',
                background: 'linear-gradient(135deg, #22c55e, #0f4d2e)',
                color: 'white', fontWeight: '700', cursor: 'pointer', fontSize: '0.875rem',
              }}>
                Yes, Switch to {selectedPlan?.name}
              </button>
              <button onClick={() => setConfirming(false)} style={{
                padding: '0.6rem 1.5rem', borderRadius: '8px', border: 'none',
                background: '#4b5563', color: 'white', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem',
              }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        {!confirming && (
          <div style={{
            padding: '1.25rem 2rem 2rem',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderTop: '1px solid #f1f5f9',
          }}>
            <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
              No credit card changes until Stripe is connected
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={onClose} style={{
                padding: '0.65rem 1.5rem', borderRadius: '9px', border: 'none',
                background: '#4b5563', color: 'white', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem',
              }}>Cancel</button>
              <button onClick={handleConfirm} disabled={selected === currentPlan} style={{
                padding: '0.65rem 1.75rem', borderRadius: '9px', border: 'none',
                background: selected === currentPlan ? '#e2e8f0' : 'linear-gradient(135deg, #22c55e, #0f4d2e)',
                color: selected === currentPlan ? '#94a3b8' : 'white',
                fontWeight: '700', cursor: selected === currentPlan ? 'not-allowed' : 'pointer', fontSize: '0.875rem',
              }}>
                {selected === currentPlan ? 'No Changes' : `Switch to ${selectedPlan?.name} →`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
